import { type App, type DirectiveBinding } from 'vue'
import emitter from './mitt'
import { ElNotification } from 'element-plus'
import { isEqual } from 'lodash'
import login from '@/components/login'
import { ytPushDirective } from './yt-push-directive'

interface wordSelection {
  oneWord?: boolean
  text: string
  termName?: string
  /**题目id（收藏单词时关联题目，存入 qid） */
  qid?: number
  onSelectWord?: (word: string) => void
  /** 变化时强制重跑一次渲染（如「重做」后清掉上一轮的选句高亮） */
  resetKey?: number
}

/**
 * 每个宿主元素最近一次渲染的令牌。
 * wordParaphrase 走 requestIdleCallback 延后处理，若期间又切到一句话模式，
 * selectWord 已同步改写 innerHTML 与 onclick，迟到的回调会把选词视图覆盖成
 * 「全文查词」视图，表现为点句子选不了、点单词只会查词。
 * 每次渲染自增令牌，延迟回调执行前校验，过期即丢弃。
 */
const renderTokenMap = new WeakMap<HTMLElement, number>()

function bumpRenderToken(el: HTMLElement) {
  const token = (renderTokenMap.get(el) || 0) + 1
  renderTokenMap.set(el, token)
  return token
}

export default function myDirective(app: App<Element>) {
  app.directive('word-selection', (el: HTMLDivElement, binding) => {
    const value = binding.value as wordSelection | undefined
    // 判断oneWord和text是否被改变
    if (value && binding.oldValue) {
      // 判断对象值是否相等
      if (isEqual(binding.oldValue, binding.value)) {
        return
      }
    }
    // 换令牌，作废可能还在排队的上一次延迟渲染
    const token = bumpRenderToken(el)
    if (value?.oneWord) {
      selectWord(el, binding)
    } else {
      wordParaphrase(el, binding, token)
    }
  })
  app.directive('auth-login', (el: HTMLDivElement, binding) => {
    el.onclick = () => {
      if (Boolean(localStorage.getItem('token'))) {
        binding.value()
      } else {
        login()
      }
    }
  })
  app.directive('auto-width', {
    beforeMount(el) {
      // 创建一个隐藏的测试元素，用来测量文本宽度
      let tester = document.createElement('div')
      tester.style.position = 'absolute'
      tester.style.visibility = 'hidden'
      tester.style.fontSize = '18px'
      tester.style.whiteSpace = 'pre'
      document.body.appendChild(tester)
      let adjustWidth = () => {
        // 将输入框的内容设置到测试元素中，并获取其宽度
        tester.textContent = el.value
        let width = tester.getBoundingClientRect().width
        // 将宽度应用到输入框上，添加一点额外的空间以防止文字紧贴输入框边缘
        el.style.width = `${width}px`
      }

      // 首次绑定时调整一次宽度
      adjustWidth()

      // 在输入事件上添加监听器，动态调整宽度
      el.addEventListener('input', adjustWidth)

      // 在卸载元素时移除监听器和测试元素
      el._destroyAutoWidth = () => {
        el.removeEventListener('input', adjustWidth)
        document.body.removeChild(tester)
      }
    },
    beforeUnmount(el) {
      // 调用在 beforeMount 阶段保存的销毁函数
      el._destroyAutoWidth()
    }
  })
  // 埋点（指令定义抽在 ./yt-push-directive，供主应用与独立 createApp 弹窗应用共用）
  app.directive('yt-push', ytPushDirective)
}

/** 解码常见 HTML 实体：命名实体 + &#39; / &#x27; 等数字实体，避免实体被分词逻辑拆解 */
function decodeHtmlEntities(content: string) {
  return content
    .replace(/&#x([0-9a-fA-F]+);/g, (match, hex) => {
      const code = parseInt(hex, 16)
      return code <= 0x10ffff ? String.fromCodePoint(code) : match
    })
    .replace(/&#(\d+);/g, (match, dec) => {
      const code = Number(dec)
      return code <= 0x10ffff ? String.fromCodePoint(code) : match
    })
    .replace(/&rsquo;/g, "'")
    .replace(/&nbsp;|0xA0/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
}

/** 一句话选词：与全文释义同源字段常为 HTML，需先抽成含空格正文再按空格切词 */
function toPlainTextForWordSplit(raw: string) {
  if (!raw) return ''
  let s = decodeHtmlEntities(
    raw
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/(p|div|li|tr|h[1-6])\s*>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
  )
  return s.replace(/\s+/g, ' ').trim()
}

// 选词模式
function selectWord(el: HTMLDivElement, binding: DirectiveBinding<wordSelection>) {
  // 一句话模式的排版与选句高亮样式都挂在 #word-selection 下；未经过全文渲染
  // 直接进入该模式（如组件重挂载时 oneWord 已为 true）需自行补 id，
  // 否则词与词之间没有间距会叠成一团
  el.id = 'word-selection'
  el.classList.add('one-word')
  let wordIdx = 0
  const plain = toPlainTextForWordSplit(binding.value.text || '')
  const html = plain
    .split(' ')
    .filter(Boolean)
    .map((item) => {
      if (isSymbol(item)) {
        return `<span  class="word symbol">${item}</span>`
      } else {
        wordIdx += 1
        return `<span data-index="${wordIdx}" class="word">${item}</span>`
      }
    })
    .join('')
  el.innerHTML = html
  let st = -1
  let end = -1
  el.onclick = (event) => {
    const wordDom = event.target as HTMLSpanElement

    if (wordDom.nodeName === 'SPAN' && wordDom.className.indexOf('word') !== -1) {
      const wordDomIndex = Number(wordDom.dataset.index)
      if (st === -1 || (st !== -1 && end !== -1)) {
        st = wordDomIndex
        end = -1
      } else if (end === -1) {
        end = wordDomIndex
        // 限制选词不能少于10个
        if (Math.abs(st - wordDomIndex) < 10 - 1) {
          st = wordDomIndex
          ElNotification({
            offset: 100,
            title: '一句话模式使用',
            message: '点击想选句子首位和末尾单词进行练习，最少选10个词'
          })
        }
      }

      ;[...el.querySelectorAll('.word')].forEach((item) => {
        item.classList.remove('st', 'end', 'active')
      })
      ;[...el.querySelectorAll('.word')].forEach((item) => {
        const idx = Number((item as HTMLSpanElement).dataset.index)
        if (idx === st) {
          item.classList.add('st')
        }
        if (idx === end) {
          item.classList.add('end')
        }
      })

      if (st !== -1 && end !== -1) {
        let c = -1
        if (end < st) {
          c = end
          end = st
          st = c
          const stDom = el.querySelector('.word.st')
          const endDom = el.querySelector('.word.end')
          stDom?.classList.remove('st')
          stDom?.classList.add('end')
          endDom?.classList.remove('end')
          endDom?.classList.add('st')
        }
        ;[...el.querySelectorAll('.word')].forEach((item) => {
          const idx = Number((item as HTMLSpanElement).dataset.index)
          if (idx >= st && idx <= end) {
            item.classList.add('active')
          }
        })
        ;[...el.querySelectorAll('.symbol')].forEach((item) => {
          if (
            item.previousElementSibling?.classList.contains('active') &&
            item.nextElementSibling?.classList.contains('active')
          ) {
            item.classList.add('active')
          }
        })
        const selectTex = ([...el.querySelectorAll('.word.active')] as HTMLSpanElement[])
          .map((item) => item.innerText + ' ')
          .join('')
        binding.value.onSelectWord && binding.value.onSelectWord(selectTex)
      }
    }
  }
}
// 单词释义模式
function wordParaphrase(
  el: HTMLDivElement,
  binding: DirectiveBinding<wordSelection>,
  token: number
) {
  var Regx = /^[A-Za-z0-9]/
  // 切回全文模式时同步摘掉一句话排版（延迟处理前先摘，避免排版闪一下）
  el.classList.remove('one-word')
  if (!binding.value.text) return

  const process = () => {
    // 本次渲染已被后续指令调用取代（例如刚切到一句话模式），直接丢弃
    if (renderTokenMap.get(el) !== token) return
    var content = decodeHtmlEntities(binding.value.text)
    var messageCon = content.split(/(<[^>]+>)/g)
    var DataS = []
    let isHtml = false
    for (let info of messageCon) {
      if (info.search(/(<[^>]+>)/g) < 0) {
        // 修改点：允许单词包含连字符
        const placeholder = 'HTMLENTITYPLACEHOLDER'
        // 保护命名实体与数字实体（如 &rsquo;、&#39;、&#x27;），避免被分词正则拆解
        const entityRegex = /&(?:[a-zA-Z]+|#\d+|#x[0-9a-fA-F]+);/g

        const entities: string[] = []
        const tempInfo = info.replace(entityRegex, (match) => {
          entities.push(match)
          return placeholder
        })

        // 修改点：允许单词中包含连字符（-）
        const wordRegex = /\b[\w'-]+\b/g
        const intermediateResult = tempInfo.replace(wordRegex, `<span class="word">$&</span>`)

        let entityIndex = 0
        const finalResult = intermediateResult.replace(
          new RegExp(placeholder, 'g'),
          () => entities[entityIndex++]
        )
        info = finalResult
      } else {
        isHtml = true
      }
      DataS.push(info)
    }
    if (isHtml) {
      el.style.color = '#000'
    }

    el.id = 'word-selection'
    const html = decodeHtmlEntities(DataS.join(''))
    el.innerHTML = html

    el.onclick = (event) => {
      const wordDom = event.target as HTMLSpanElement
      if (
        wordDom.nodeName === 'SPAN' &&
        wordDom.className.indexOf('word') !== -1 &&
        wordDom.className.indexOf('symbol') === -1
      ) {
        if (Regx.test(wordDom.innerText)) {
          console.log('wordParaphrase termName:' + binding.value.termName)
          emitter.emit('word', {
            wordStr: wordDom.innerText,
            termName: binding.value.termName ? binding.value.termName : '',
            qid: binding.value.qid
          })
        }
      }
    }
  }

  // 使用 requestIdleCallback 延迟处理，避免阻塞主线程
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    requestIdleCallback(process, { timeout: 100 })
  } else {
    setTimeout(process, 0)
  }
}
export function wordCount(text: string) {
  return text
    .replace(/(--|\s+)/g, ' ')
    .trim()
    .split(' ')
    .filter((item) => item).length
}
function isSymbol(str: string) {
  var pattern = /^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/
  return pattern.test(str)
}
